import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { buildApiUrl } from '../config/api';
import { getApiAccessToken } from '../config/authToken';
import { Button } from './ui/button';
import { Users, User, ChevronRight } from 'lucide-react';
import { cn } from '../lib/utils';
import ClientSelectorModal from './ClientSelectorModal';

const ClientSelectorField = ({ 
  value = [], 
  onValueChange, 
  placeholder = "Выберите клиента",
  trainerId = null,
  singleSelect = true, // По умолчанию выбор одного клиента
  className,
  ...props 
}) => {
  const [showModal, setShowModal] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);

  // Получаем trainerId из пропсов или из localStorage как fallback - мемоизируем
  const effectiveTrainerId = useMemo(() => {
    if (trainerId) {
      return trainerId;
    }
    
    try {
      const savedUser = localStorage.getItem('currentUser');
      if (savedUser) {
        const user = JSON.parse(savedUser);
        return user.id;
      }
    } catch (e) {
      console.error('Error getting user from localStorage:', e);
    }
    return null;
  }, [trainerId]);

  // Мемоизируем функцию загрузки клиентов
  const loadClients = useCallback(async () => {
    if (!effectiveTrainerId) {
      return;
    }
    
    try {
      const opts = { credentials: 'include' };
      const b = getApiAccessToken();
      if (b) opts.headers = { Authorization: `Bearer ${b}` };
      const response = await fetch(buildApiUrl(`/users/${effectiveTrainerId}/clients`), opts);
      if (!response.ok) {
        throw new Error('Failed to fetch clients');
      }
      const clientsData = await response.json();
      setClients(clientsData || []);
    } catch (err) {
      console.error('Ошибка загрузки клиентов:', err);
    }
  }, [effectiveTrainerId]);

  // Загрузка клиентов для отображения имен
  useEffect(() => {
    loadClients();
  }, [loadClients]);

  // Обновление выбранных клиентов при изменении value
  useEffect(() => {
    if (clients.length > 0 && value.length > 0) {
      const selected = clients.filter(client => value.includes(client.id));
      setSelectedClients(selected);
    } else {
      setSelectedClients([]);
    }
  }, [value, clients]);

  const handleSelect = useCallback((selectedIds) => {
    onValueChange?.(selectedIds);
    // Обновляем список клиентов после выбора (на случай, если был создан новый клиент)
    loadClients();
  }, [onValueChange, loadClients]);

  // Форматирование отображения выбранных клиентов
  const getDisplayText = () => {
    if (selectedClients.length === 0) {
      return placeholder;
    }
    
    if (selectedClients.length === 1) {
      return selectedClients[0].name || selectedClients[0].nickname || `Клиент ${selectedClients[0].id}`;
    }
    
    if (selectedClients.length === 2) {
      const name1 = selectedClients[0].name || selectedClients[0].nickname || `Клиент ${selectedClients[0].id}`;
      const name2 = selectedClients[1].name || selectedClients[1].nickname || `Клиент ${selectedClients[1].id}`;
      return `${name1}, ${name2}`;
    }
    
    // Для 3+ клиентов: показываем первого и количество остальных
    const firstName = selectedClients[0].name || selectedClients[0].nickname || `Клиент ${selectedClients[0].id}`;
    const restCount = selectedClients.length - 1;
    return `${firstName} (+${restCount})`;
  };

  const Icon = singleSelect ? User : Users;

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setShowModal(true)}
        className={cn(
          "w-full justify-between min-h-[40px] h-auto text-left font-normal",
          selectedClients.length === 0 && "text-muted-foreground",
          className
        )}
        {...props}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Icon className="h-4 w-4 shrink-0" />
          <span className="truncate">{getDisplayText()}</span>
        </div>
        <ChevronRight className="h-4 w-4 shrink-0 opacity-50" />
      </Button>

      <ClientSelectorModal
        open={showModal}
        onOpenChange={useCallback((open) => {
          setShowModal(open);
          if (!open) {
            // Обновляем список клиентов при закрытии модала
            loadClients();
          }
        }, [loadClients])}
        selectedClientIds={value}
        onSelect={handleSelect}
        trainerId={effectiveTrainerId}
        singleSelect={singleSelect}
      />
    </>
  );
};

export default ClientSelectorField;
